import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  LegacyNotificationPreferenceService,
  LegacyStatus,
  LegacyPreferenceInterface,
  LegacySectionSettingsInterface,
  LegacyGetResponseInterface,
  LegacyNotificationSettingInterface,
  LegacyProductInterface,
  LegacyNotificationActivityInterface,
} from '@vendasta/notifications-sdk';
import { pushNotificationsEnabled, registerForPushNotifications, requestPermissions } from '@vendasta/shared';
import { Capacitor } from '@capacitor/core';

export const INSTANT_EMAIL_ID = 'instantEmailFlag';
export const DAILY_DIGEST_ID = 'activityDigestEmailFlag';
export const EXECUTIVE_REPORT_ID = 'executiveReportFlag';

export interface NotificationSetting {
  id: string;
  title: string;
  localized_title?: string;
  value: LegacyStatus;
  tags?: NotificationSetting[];
}

export interface NotificationSettingsProduct {
  title: string;
  activities: NotificationSetting[];
  productId?: string;
}

export interface NotificationSettingsSection {
  section: NotificationSetting;
  products: NotificationSettingsProduct[];
}

function fromAvailableSettingsSections(sections: LegacySectionSettingsInterface[]): NotificationSettingsSection[] {
  return (sections || []).map(fromAvailableSettingsSection);
}

function fromAvailableSettingsSection(section: LegacySectionSettingsInterface): NotificationSettingsSection {
  return {
    section: fromAvailableSetting(section?.section),
    products: (section?.products || []).map(fromAvailableSettingsProduct),
  };
}

function fromAvailableSetting(setting: LegacyNotificationSettingInterface): NotificationSetting {
  return {
    id: setting?.notificationId,
    title: setting?.title,
    localized_title: setting?.localizedTitle,
    value: null,
  };
}

function fromAvailableSettingsProduct(product: LegacyProductInterface): NotificationSettingsProduct {
  return {
    title: product?.title,
    activities: (product?.activities || []).map(fromAvailableActivity),
    productId: product?.productId,
  };
}

function fromAvailableActivity(activity: LegacyNotificationActivityInterface): NotificationSetting {
  return {
    id: activity?.notificationId,
    title: activity?.title,
    localized_title: activity?.localizedTitle,
    value: null,
    tags: (activity?.tags || []).map(fromAvailableSetting),
  };
}

@Injectable({
  providedIn: 'root',
})
export class NotificationPreferencesService {
  private readonly preferenceService = inject(LegacyNotificationPreferenceService);

  getSettings(
    accountGroupId: string,
    partnerId: string,
    marketId?: string,
    locale?: string,
  ): Observable<NotificationSettingsSection[]> {
    return this.preferenceService
      .getAvailableSections(partnerId, marketId, accountGroupId, locale)
      .pipe(map(fromAvailableSettingsSections));
  }

  updatePreference(
    partnerId: string,
    marketId: string,
    userId: string,
    accountGroupId: string,
    sectionId: string,
    settingId: string,
    status: LegacyStatus,
  ): Observable<unknown> {
    const changes: LegacyPreferenceInterface = { instantSettings: [], digestSettings: [], executiveReportSettings: [] };
    if (settingId === INSTANT_EMAIL_ID) {
      changes.instant = status;
    } else if (settingId === DAILY_DIGEST_ID) {
      changes.digest = status;
    } else if (sectionId === EXECUTIVE_REPORT_ID) {
      changes.executiveReportSettings.push({
        notificationId: settingId,
        status: status,
      });
    } else if (sectionId === INSTANT_EMAIL_ID) {
      changes.instantSettings.push({
        notificationId: settingId,
        status: status,
      });
    } else if (sectionId === DAILY_DIGEST_ID) {
      changes.digestSettings.push({
        notificationId: settingId,
        status: status,
      });
    }
    return this.preferenceService.update$(changes, partnerId, marketId, userId, accountGroupId);
  }

  getPreferences(
    partnerId: string,
    marketId: string,
    userId: string,
    accountGroupId: string,
  ): Observable<LegacyGetResponseInterface> {
    return this.preferenceService.get$(partnerId, marketId, userId, accountGroupId);
  }

  async pushNotificationsEnabled(): Promise<boolean> {
    return await pushNotificationsEnabled();
  }

  async registerForPushNotifications(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      return await registerForPushNotifications();
    } else {
      return await requestPermissions();
    }
  }
}
